import { useField, useForm } from "@shopify/react-form";
import { Dispatch, SetStateAction, useRef } from "react";
import { BadgeType, CartConfigInterface, initialCartConfig } from "../CartTypes";
import {
    AvailableCartDrawerTemplates,
    AvailableCartFooterLayouts,
    FontStylesTypes,
    HeaderHeightTypes,
    validateFontSize,
    validateHexalColor,
    validateTextLength,
    validateTrustBadges,
} from "../../Bundles/constants";
import { custom_field } from "../../Bundles/Types";

const mergeIconStates = (defaultIcons: BadgeType[], savedIcons: BadgeType[]) => {
    return defaultIcons.map((icon) => {
        const saved = savedIcons.find((i) => i.originalSrc === icon.originalSrc);
        return {
            ...icon,
            selected: saved?.selected ?? false,
        };
    });
};

const useCartFormFields = ({
    _config,
    setConfig,
}: {
    _config: CartConfigInterface;
    setConfig: Dispatch<SetStateAction<CartConfigInterface>>;
}) => {
    const UpsellProductType = useRef("");
    const additional_product_addon_checkbox = useRef(false);
    const custom_fields_checkbox = useRef(false);
    const tiers = useField(_config?.reward?.tiers);
    const market_tiers = useField(_config?.reward?.market_tiers);

    const { fields, submit, reset, dirty, makeClean, submitErrors, validate, submitting } = useForm({
        fields: {
            cart_status: useField(_config?.cart_status),
            selected_drawer_template: useField(
                _config?.selected_drawer_template || AvailableCartDrawerTemplates.BASIC_LAYOUT,
            ),
            selected_drawer_footer_layout: useField(
                _config?.selected_drawer_footer_layout || AvailableCartFooterLayouts.HORIZONTAL,
            ),
            selected_drawer_color: useField(
                _config?.selected_drawer_color ||
                    JSON.stringify({
                        alpha: 1,
                        brightness: 0,
                        hex: "#000000",
                        hue: 0,
                        saturation: 0,
                    }),
            ),
            cart_id: useField(_config?.cart_id),
            cart_title: useField({
                value: _config?.cart_title,
                validates: [
                    (val) => {
                        if (!val) {
                            return "Cart title is required";
                        }
                        if (val.length < 3 || val.length > 40) {
                            return "Invalid cart title length";
                        } else {
                            return "";
                        }
                    },
                ],
            }),
            checkout_cta_text: useField({
                value: _config?.checkout_cta_text,
                validates: [
                    (val) => {
                        if (!val) {
                            return "Checkout button text is required";
                        }
                        if (val.length < 3 || val.length > 40) {
                            return "Invalid checkout button text length";
                        } else {
                            return "";
                        }
                    },
                ],
            }),
            checkout_icon_url: useField(_config?.checkout_icon_url),
            upsell_cta_icon_url: useField(_config?.upsell_cta_icon_url),
            empty_cart_text: useField({
                value: _config?.empty_cart_text,
                validates: [
                    (val) => {
                        if (!val) {
                            return "Empty cart text is required";
                        }
                        if (val.length < 3 || val.length > 40) {
                            return "Invalid empty cart text length";
                        } else {
                            return "";
                        }
                    },
                ],
            }),
            continue_shopping_cta_text: useField({
                value: _config?.continue_shopping_cta_text,
                validates: [
                    (val) => {
                        if (!val) {
                            return "Continue Shopping CTA text is required";
                        }
                        if (val.length < 3 || val.length > 40) {
                            return "Invalid Continue Shopping CTA text length";
                        } else {
                            return "";
                        }
                    },
                ],
            }),
            applied_discount_text: useField({
                value: _config?.applied_discount_text,
                validates: [
                    (val) => {
                        if (!val) {
                            return "Applied discount text is required";
                        }
                        if (val.length < 3 || val.length > 40) {
                            return "Invalid applied discount text length";
                        } else {
                            return "";
                        }
                    },
                ],
            }),
            add_upsell_text: useField({
                value: _config?.add_upsell_text,
                validates: [
                    (val) => {
                        if (!val) {
                            return "Add upsell text is required";
                        }
                        return undefined;
                    },
                ],
            }),
            reward_free_gift_text: useField({
                value: _config?.reward_free_gift_text,
                validates: [
                    (val) => {
                        if (!val) {
                            return "Grab your Free Gift text is required";
                        }
                        return undefined;
                    },
                ],
            }),
            estimated_total_text: useField({
                value: _config?.estimated_total_text,
                validates: [
                    (val) => {
                        if (!val) {
                            return "Estimated total text is required";
                        }
                        return undefined;
                    },
                ],
            }),
            show_underline_below_cart_text: useField(_config?.show_underline_below_cart_text ?? true),
            order_discount_message_text: useField({
                value: _config?.order_discount_message_text,
                validates: [
                    (val) => {
                        if (!val) {
                            return "Order Discount Message text is required";
                        }
                        return undefined;
                    },
                ],
            }),
            bill_summary_text: useField({
                value: _config?.bill_summary_text,
                validates: [
                    (val) => {
                        if (!val) {
                            return "Bill Summary text is required";
                        }
                        return undefined;
                    },
                ],
            }),
            item_total_text: useField({
                value: _config?.item_total_text,
                validates: [
                    (val) => {
                        if (!val) {
                            return "Item Total text is required";
                        }
                        return undefined;
                    },
                ],
            }),
            shipping_text: useField({
                value: _config?.shipping_text,
                validates: [
                    (val) => {
                        if (!val) {
                            return "Shipping text is required";
                        }
                        return undefined;
                    },
                ],
            }),
            shipping_message_text: useField({
                value: _config?.shipping_message_text,
                validates: [
                    (val) => {
                        if (!val) {
                            return "Shipping message text is required";
                        }
                        return undefined;
                    },
                ],
            }),
            cart_discounts_text: useField({
                value: _config?.cart_discounts_text,
                validates: [
                    (val) => {
                        if (!val) {
                            return "Cart Discount text is required";
                        }
                        return undefined;
                    },
                ],
            }),
            grand_total_text: useField({
                value: _config?.grand_total_text,
                validates: [
                    (val) => {
                        if (!val) {
                            return "Grand total text is required";
                        }
                        return undefined;
                    },
                ],
            }),
            view_cart_cta_text: useField({
                value: _config?.view_cart_cta_text,
                validates: [
                    (val) => {
                        if (!val) {
                            return "View cart text is required";
                        }
                        if (val.length < 3 || val.length > 40) {
                            return "Invalid view cart text length";
                        } else {
                            return "";
                        }
                    },
                ],
            }),
            cart_item_text: useField(_config?.cart_item_text),
            cart_items_text: useField(_config?.cart_items_text),
            continue_shop_cta_text: useField({
                value: _config?.continue_shop_cta_text,
                validates: [
                    (val) => {
                        if (!val) {
                            return "Continue shopping text is required";
                        }
                        if (val.length < 3 || val.length > 40) {
                            return "Invalid continue shopping text length";
                        } else {
                            return "";
                        }
                    },
                ],
            }),
            saving_label_text: useField({
                value: _config?.saving_label_text,
                validates: [
                    (val) => {
                        if (!val) {
                            return "Saving text is required";
                        }
                        if (val.length < 3 || val.length > 40) {
                            return "Invalid saving text length";
                        } else {
                            return "";
                        }
                    },
                ],
            }),
            free_gift_cta: useField({
                value: _config?.free_gift_cta,
                validates: [
                    (val) => {
                        if (!val) {
                            return "Free gift CTA text is required";
                        }
                        if (val.length < 3 || val.length > 40) {
                            return "Invalid free gift CTA text length";
                        } else {
                            return "";
                        }
                    },
                ],
            }),
            free_gift_free_label: useField({
                value: _config?.free_gift_free_label,
                validates: [
                    (val) => {
                        if (!val) {
                            return "Free gift free label is required";
                        }
                        if (val.length < 3 || val.length > 40) {
                            return "Invalid free gift free label length";
                        } else {
                            return "";
                        }
                    },
                ],
            }),
            cart_features_positions: useField(_config?.cart_features_positions),
            cart_design: {
                cart_font_family: useField(_config?.cart_design?.cart_font_family),
                cart_bg_color: useField({
                    value: _config?.cart_design?.cart_bg_color,
                    validates: [(val) => validateHexalColor(val, "Cart drawer background")],
                }),
                cart_primary_color: useField(
                    _config.cart_design.cart_primary_color || initialCartConfig?.cart_design?.cart_primary_color,
                ),
                cart_lighten_primary_color: useField(
                    _config.cart_design.cart_lighten_primary_color ||
                        initialCartConfig?.cart_design?.cart_lighten_primary_color,
                ),
                cart_gradient_color: useField(
                    _config.cart_design.cart_gradient_color || initialCartConfig?.cart_design?.cart_gradient_color,
                ),
                cart_width: useField(_config?.cart_design?.cart_width),
                header_bg_color: useField({
                    value: _config?.cart_design?.header_bg_color,
                    validates: [(val) => validateHexalColor(val, "Cart header background")],
                }),
                header_title_color: useField({
                    value: _config?.cart_design?.header_title_color,
                    validates: [(val) => validateHexalColor(val, "Cart header background")],
                }),
                header_title_underline_color: useField({
                    value: _config?.cart_design?.header_title_underline_color,
                    validates: [(val) => validateHexalColor(val, "Cart Title Underline")],
                }),
                header_title_underline_color_gradient_status: useField(
                    _config?.cart_design?.header_title_underline_color_gradient_status,
                ),
                header_title_underline_color_gradient_color: useField({
                    value: _config?.cart_design?.header_title_underline_color_gradient_color,
                    validates: [(val) => validateHexalColor(val, "Cart Title Gradient")],
                }),
                header_title_underline_color_gradient_direction: useField(
                    _config?.cart_design?.header_title_underline_color_gradient_direction,
                ),
                header_quantity_color: useField({
                    value: _config?.cart_design?.header_quantity_color,
                    validates: [(val) => validateHexalColor(val, "Close button")],
                }),
                header_height: useField<HeaderHeightTypes>(_config?.cart_design?.header_height),
                close_button_color: useField({
                    value: _config?.cart_design?.close_button_color,
                    validates: [(val) => validateHexalColor(val, "Close button")],
                }),
                // CartLine Card Starts
                cartline_card_bg_color: useField({
                    value: _config?.cart_design?.cartline_card_bg_color,
                    validates: [(val) => validateHexalColor(val, "Product card background")],
                }),
                cartline_card_text_color: useField({
                    value: _config?.cart_design?.cartline_card_text_color,
                    validates: [(val) => validateHexalColor(val, "Product card text")],
                }),
                cartline_card_save_text_color: useField({
                    value: _config?.cart_design?.cartline_card_save_text_color,
                    validates: [(val) => validateHexalColor(val, "Product card save text")],
                }),
                cartline_card_qty_color: useField({
                    value: _config?.cart_design?.cartline_card_qty_color,
                    validates: [(val) => validateHexalColor(val, "Product card quantity text")],
                }),
                cartline_card_discount_bg_color: useField({
                    value: _config?.cart_design?.cartline_card_discount_bg_color,
                    validates: [(val) => validateHexalColor(val, "Product card discount background")],
                }),
                cartline_card_discount_text_color: useField({
                    value: _config?.cart_design?.cartline_card_discount_text_color,
                    validates: [(val) => validateHexalColor(val, "Product card discount text")],
                }),
                cartline_card_title_size: useField({
                    value: _config?.cart_design?.cartline_card_title_size,
                    validates: [(val) => validateFontSize(val, "Product Title")],
                }),
                cartline_card_title_style: useField<FontStylesTypes>(_config?.cart_design?.cartline_card_title_style),
                cartline_card_properties_size: useField({
                    value: _config?.cart_design?.cartline_card_properties_size,
                    validates: [(val) => validateFontSize(val, "Product Properties")],
                }),
                cartline_card_properties_style: useField<FontStylesTypes>(
                    _config?.cart_design?.cartline_card_properties_style,
                ),
                cartline_card_price_size: useField({
                    value: _config?.cart_design?.cartline_card_price_size,
                    validates: [(val) => validateFontSize(val, "Product Price")],
                }),
                cartline_card_price_style: useField<FontStylesTypes>(_config?.cart_design?.cartline_card_price_style),
                cartline_card_compared_price_size: useField({
                    value: _config?.cart_design?.cartline_card_compared_price_size,
                    validates: [(val) => validateFontSize(val, "Product Card Compared-at")],
                }),
                cartline_card_compared_price_style: useField<FontStylesTypes>(
                    _config?.cart_design?.cartline_card_compared_price_style,
                ),
                cartline_card_save_text_size: useField({
                    value: _config?.cart_design?.cartline_card_save_text_size,
                    validates: [(val) => validateFontSize(val, "Product Card Saved Text")],
                }),
                cartline_card_save_text_style: useField<FontStylesTypes>(
                    _config?.cart_design?.cartline_card_save_text_style,
                ),
                // CartLine Card Ends
                footer_text_color: useField({
                    value: _config?.cart_design?.footer_text_color,
                    validates: [(val) => validateHexalColor(val, "Footer price text")],
                }),
                footer_text_size: useField({
                    value: _config?.cart_design?.footer_text_size,
                    validates: [(val) => validateFontSize(val, "Footer Total Price")],
                }),
                footer_text_style: useField(_config?.cart_design?.footer_text_style),
                footer_compare_at_color: useField({
                    value: _config?.cart_design?.footer_compare_at_color,
                    validates: [(val) => validateHexalColor(val, "Footer compare-at price text")],
                }),
                footer_compare_at_size: useField({
                    value: _config?.cart_design?.footer_compare_at_size,
                    validates: [(val) => validateFontSize(val, "Footer Compare-at Price")],
                }),
                footer_compare_at_style: useField(_config?.cart_design?.footer_compare_at_style),
                footer_secondary_text_color: useField({
                    value: _config?.cart_design?.footer_secondary_text_color,
                    validates: [(val) => validateHexalColor(val, "Footer Secondary Text")],
                }),
                footer_secondary_text_size: useField({
                    value: _config?.cart_design?.footer_secondary_text_size,
                    validates: [(val) => validateFontSize(val, "Footer Secondary Text")],
                }),
                footer_secondary_text_style: useField(_config?.cart_design?.footer_secondary_text_style),
                footer_bg_color: useField({
                    value: _config?.cart_design?.footer_bg_color,
                    validates: [(val) => validateHexalColor(val, "Cart Footer Background")],
                }),
                footer_button_radius: useField(_config?.cart_design?.footer_button_radius),
                footer_button_bg_color: useField({
                    value: _config?.cart_design?.footer_button_bg_color,
                    validates: [(val) => validateHexalColor(val, "Button Background")],
                }),
                footer_button_cta_text_size: useField({
                    value: _config?.cart_design?.footer_button_cta_text_size,
                    validates: [(val) => validateFontSize(val, "Footer Button Text")],
                }),

                footer_button_cta_text_style: useField(_config?.cart_design?.footer_button_cta_text_style),
                footer_button_border_color: useField({
                    value: _config?.cart_design?.footer_button_border_color,
                    validates: [(val) => validateHexalColor(val, "Footer Button Border")],
                }),
                footer_button_hover_border_color: useField({
                    value: _config?.cart_design?.footer_button_hover_border_color,
                    validates: [(val) => validateHexalColor(val, "Footer Button Hover Border")],
                }),
                footer_button_text_color: useField({
                    value: _config?.cart_design?.footer_button_text_color,
                    validates: [(val) => validateHexalColor(val, "Button")],
                }),
                footer_custom_shipping_button_color: useField({
                    value: _config?.cart_design?.footer_custom_shipping_button_color,
                    validates: [(val) => validateHexalColor(val, "Secondary button text")],
                }),
                footer_button_bg_hover_color: useField({
                    value: _config?.cart_design?.footer_button_bg_hover_color,
                    validates: [(val) => validateHexalColor(val, "Button hover background")],
                }),
                footer_button_text_hover_color: useField({
                    value: _config?.cart_design?.footer_button_text_hover_color,
                    validates: [(val) => validateHexalColor(val, "Button hover")],
                }),
                footer_discount_tag_bg_color: useField({
                    value: _config?.cart_design?.footer_discount_tag_bg_color,
                    validates: [(val) => validateHexalColor(val, "Footer discount tag background")],
                }),
                footer_discount_tag_text_color: useField({
                    value: _config?.cart_design?.footer_discount_tag_text_color,
                    validates: [(val) => validateHexalColor(val, "Footer discount tag text")],
                }),
            },
            cart_settings: {
                redirect_to_checkout: useField(_config?.cart_settings?.redirect_to_checkout),
                show_text_below_add_to_cart: useField(_config?.cart_settings?.show_text_below_add_to_cart),
                text_below_add_to_cart: useField(_config?.cart_settings?.text_below_add_to_cart),
                show_cart_cta: useField(_config?.cart_settings?.show_cart_cta),
                cart_position: useField(_config?.cart_settings?.cart_position),
                show_continue_shopping_cta: useField(_config?.cart_settings?.show_continue_shopping_cta),
                open_cart_on_product_add: useField(_config?.cart_settings?.open_cart_on_product_add),
                show_swatches: useField(_config?.cart_settings?.show_swatches),
                show_compare_at: useField(_config?.cart_settings?.show_compare_at),
                sticky_footer: useField(_config?.cart_settings?.sticky_footer),
                show_total_saved: useField(_config?.cart_settings?.show_total_saved),
                custom_html_position: useField(_config?.cart_settings?.custom_html_position),
                custom_html_text: useField(_config?.cart_settings?.custom_html_text),
                custom_css_text: useField(_config?.cart_settings?.custom_css_text),
                show_discounts_form_compare_at: useField(_config?.cart_settings?.show_discounts_form_compare_at),
                hide_product_variant_label: useField(_config?.cart_settings?.hide_product_variant_label),
                show_product_properties: useField(_config?.cart_settings?.show_product_properties),
                show_compare_at_price: useField(_config?.cart_settings?.show_compare_at_price ?? false),
            },
            discount_combinations: {
                upsell_order_discounts: useField(_config?.discount_combinations?.upsell_order_discounts),
                upsell_product_discounts: useField(_config?.discount_combinations?.upsell_product_discounts),
                upsell_shipping_discounts: useField(_config?.discount_combinations?.upsell_shipping_discounts),
                reward_free_gift_order_discounts: useField(
                    _config?.discount_combinations?.reward_free_gift_order_discounts,
                ),
                reward_free_gift_product_discounts: useField(
                    _config?.discount_combinations?.reward_free_gift_product_discounts,
                ),
                reward_free_gift_shipping_discounts: useField(
                    _config?.discount_combinations?.reward_free_gift_shipping_discounts,
                ),
                reward_order_order_discounts: useField(_config?.discount_combinations?.reward_order_order_discounts),
                reward_order_product_discounts: useField(
                    _config?.discount_combinations?.reward_order_product_discounts,
                ),
                reward_order_shipping_discounts: useField(
                    _config?.discount_combinations?.reward_order_shipping_discounts,
                ),
            },
            reward: {
                status: useField(
                    {
                        value: _config?.reward?.status,
                        validates: (status) => {
                            if (status) {
                                const hasMarketTiers =
                                    !!market_tiers.value &&
                                    Object.values(market_tiers.value).some((tiers) => !!tiers.length);

                                if (!tiers.value.length && !hasMarketTiers)
                                    return "At least one reward tier is required";

                                if (market_tiers.value) {
                                    for (const tiers of Object.values(market_tiers.value)) {
                                        for (const tier of tiers ?? []) {
                                            if (tier.type == "product" && tier.free_products.length == 0) {
                                                return "Free product reward is required.";
                                            }
                                        }
                                    }
                                }

                                for (const tier of tiers.value) {
                                    if (tier.type == "product" && tier.free_products.length == 0) {
                                        return "Free product reward is required.";
                                    }
                                }
                            }
                        },
                    },
                    [market_tiers.value, tiers.value],
                ),
                show_reward_on_empty_cart: useField(_config?.reward?.show_reward_on_empty_cart),
                enable_automatic_currency_conversion: useField(_config?.reward?.enable_automatic_currency_conversion),
                text_after_reward_completion: useField(_config?.reward?.text_after_reward_completion),
                reward_basis: useField(_config?.reward?.reward_basis),
                tiers,
                market_tiers,
                auto_add_free_gifts: useField(_config?.reward?.auto_add_free_gifts),
            },
            reward_design: {
                bar_background_color: useField({
                    value: _config?.reward_design?.bar_background_color,
                    validates: [(val) => validateHexalColor(val, "Progress Bar background")],
                }),
                bar_foreground_color: useField({
                    value: _config?.reward_design?.bar_foreground_color,
                    validates: [(val) => validateHexalColor(val, "Progress Bar foreground")],
                }),
                bar_foreground_gradient_status: useField(_config?.reward_design?.bar_foreground_gradient_status),
                bar_foreground_gradient_direction: useField(_config?.reward_design?.bar_foreground_gradient_direction),
                bar_foreground_gradient_color: useField({
                    value: _config?.reward_design?.bar_foreground_gradient_color,
                    validates: [(val) => validateHexalColor(val, "Progress Bar Gradient background")],
                }),
                free_gift_bg_color: useField({
                    value: _config?.reward_design?.free_gift_bg_color,
                    validates: [(val) => validateHexalColor(val, "Free Gift Product background")],
                }),
                free_gift_button_bg_color: useField({
                    value: _config?.reward_design?.free_gift_button_bg_color,
                    validates: [(val) => validateHexalColor(val, "Free Gift Button background")],
                }),
                free_gift_button_text_color: useField({
                    value: _config?.reward_design?.free_gift_button_text_color,
                    validates: [(val) => validateHexalColor(val, "Free Gift Button Text")],
                }),
                reward_free_gift_text_color: useField({
                    value:
                        _config?.reward_design?.reward_free_gift_text_color ||
                        initialCartConfig?.reward_design?.reward_free_gift_text_color,
                    validates: [(val) => validateHexalColor(val, "Free Gift Product Card Header Text")],
                }),
                reward_free_gift_product_title_color: useField({
                    value:
                        _config?.reward_design?.reward_free_gift_product_title_color ||
                        initialCartConfig?.reward_design?.reward_free_gift_product_title_color,
                    validates: [(val) => validateHexalColor(val, "Free Gift Product title Text")],
                }),
                progress_message_text_color: useField({
                    value:
                        _config?.reward_design?.progress_message_text_color ||
                        initialCartConfig?.reward_design?.progress_message_text_color,
                    validates: [(val) => validateHexalColor(val, "Reward Progress Text")],
                }),
                free_gift_free_label_color: useField({
                    value:
                        _config?.reward_design?.free_gift_free_label_color ||
                        initialCartConfig?.reward_design?.free_gift_free_label_color,
                    validates: [(val) => validateHexalColor(val, "Free Gift Free Label Color")],
                }),
            },
            triggered_rewards: {
                status: useField(_config?.triggered_rewards?.status ?? false),
                tiers: useField(_config?.triggered_rewards?.tiers || []),
            },
            upsell: {
                upsell_products_type: useField(_config?.upsell?.upsell_products_type),
                recommendation_algorithm: useField(_config?.upsell?.recommendation_algorithm),
                exclude_ai_recommendation_products: useField(_config?.upsell?.exclude_ai_recommendation_products),
                show_if_item_in_cart: useField(_config?.upsell?.show_if_item_in_cart),
                limit_upsells_in_cart: useField(_config?.upsell?.limit_upsells_in_cart),
                max_upsell_items: useField(_config?.upsell?.max_upsell_items),
                upsell_title: useField({
                    value: _config?.upsell?.upsell_title,
                    validates: [
                        (val) => {
                            if (!val) {
                                return "Upsell Title is required";
                            }
                        },
                    ],
                }),
                additional_text: useField(_config?.upsell?.additional_text),
                upsell_position: useField(_config?.upsell?.upsell_position),
                upsell_layout: useField(_config?.upsell?.upsell_layout),
                upsell_layout_dir: useField(_config?.upsell?.upsell_layout_dir),
                is_upsell_sticky: useField(_config?.upsell?.is_upsell_sticky),
                upsell_status: useField(_config?.upsell?.upsell_status),
                upsell_offers: useField({
                    value: _config?.upsell?.upsell_offers,
                    validates: [
                        (offers) => {
                            if (UpsellProductType.current == "manual" && offers.length <= 0) {
                                return "Upsells are required";
                            }
                        },
                    ],
                }),
                upsell_triggered_product_required: useField(!!_config?.upsell?.upsell_triggered_product_required),
                limit_visible_upsells_in_cart: useField(!!_config?.upsell?.limit_visible_upsells_in_cart),
                visible_upsell_limit: useField(_config?.upsell?.visible_upsell_limit),
            },
            upsell_design: {
                upsell_title_size: useField({
                    value: _config?.upsell_design?.upsell_title_size,
                    validates: [(val) => validateFontSize(val, "Upsell Title")],
                }),
                upsell_price_size: useField({
                    value: _config?.upsell_design?.upsell_price_size,
                    validates: [(val) => validateFontSize(val, "Upsell Price")],
                }),
                upsell_price_style: useField(_config?.upsell_design?.upsell_price_style),
                upsell_compared_price_size: useField({
                    value: _config?.upsell_design?.upsell_compared_price_size,
                    validates: [(val) => validateFontSize(val, "Upsell Compare-at Price")],
                }),
                upsell_compared_price_style: useField(_config?.upsell_design?.upsell_compared_price_style),
                upsell_discount_size: useField({
                    value: _config?.upsell_design?.upsell_discount_size,
                    validates: [(val) => validateFontSize(val, "Upsell Discount Text")],
                }),
                upsell_discount_style: useField(_config?.upsell_design?.upsell_discount_style),
                upsell_title_color: useField({
                    value: _config?.upsell_design?.upsell_title_color,
                    validates: [(val) => validateHexalColor(val, "Upsell Title")],
                }),
                upsell_bg_color: useField({
                    value: _config?.upsell_design?.upsell_bg_color,
                    validates: [(val) => validateHexalColor(val, "Upsell Background")],
                }),
                upsell_product_bg_color: useField({
                    value: _config?.upsell_design?.upsell_product_bg_color,
                    validates: [(val) => validateHexalColor(val, "Upsell Product background")],
                }),
                upsell_product_border_color: useField({
                    value: _config?.upsell_design?.upsell_product_border_color,
                    validates: [(val) => validateHexalColor(val, "Upsell Product Border")],
                }),
                upsell_product_text_color: useField({
                    value: _config?.upsell_design?.upsell_product_text_color,
                    validates: [(val) => validateHexalColor(val, "Upsell Product Text")],
                }),
                upsell_product_title_size: useField({
                    value: _config?.upsell_design?.upsell_product_title_size,
                    validates: [(val) => validateFontSize(val, "Upsell Product title")],
                }),
                upsell_product_title_style: useField(_config?.upsell_design?.upsell_product_title_style),
                upsell_product_discount_text_color: useField({
                    value: _config?.upsell_design?.upsell_product_discount_text_color,
                    validates: [(val) => validateHexalColor(val, "Upsell Product Discount Text")],
                }),
                upsell_title_style: useField(_config?.upsell_design?.upsell_title_style),
                upsell_button_bg_color: useField({
                    value: _config?.upsell_design?.upsell_button_bg_color,
                    validates: [(val) => validateHexalColor(val, "Upsell Button background")],
                }),
                upsell_button_text_color: useField({
                    value: _config?.upsell_design?.upsell_button_text_color,
                    validates: [(val) => validateHexalColor(val, "Upsell Button")],
                }),
                upsell_button_radius: useField({
                    value: _config?.upsell_design?.upsell_button_radius,
                    validates: [],
                }),
                upsell_card_radius: useField({
                    value: _config?.upsell_design?.upsell_card_radius,
                    validates: [],
                }),
                upsell_button_border_color: useField({
                    value: _config?.upsell_design?.upsell_button_border_color,
                    validates: [(val) => validateHexalColor(val, "Upsell Button Border")],
                }),
                upsell_button_hover_border_color: useField({
                    value: _config?.upsell_design?.upsell_button_hover_border_color,
                    validates: [(val) => validateHexalColor(val, "Upsell Button Hover Border")],
                }),
                swatches_select_color: useField(_config?.upsell_design?.swatches_select_color),
                upsell_button_bg_hover_color: useField({
                    value: _config?.upsell_design?.upsell_button_bg_hover_color,
                    validates: [(val) => validateHexalColor(val, "Upsell Button Hover background")],
                }),
                upsell_button_text_hover_color: useField({
                    value: _config?.upsell_design?.upsell_button_text_hover_color,
                    validates: [(val) => validateHexalColor(val, "Upsell Button Hover")],
                }),
            },
            announcement: {
                announcement_message_1: useField({
                    value: _config?.announcement?.announcement_message_1,
                    validates: (val) => {
                        if (!val) {
                            return "Announcement Text is required";
                        }
                    },
                }),
                announcement_message_2: useField({
                    value: _config?.announcement?.announcement_message_2,
                    validates: (val) => {
                        if (!val) {
                            return "Message 2 is required";
                        }
                    },
                }),
                announcement_status: useField(_config?.announcement?.announcement_status),
                announcement_hight: useField(_config?.announcement?.announcement_hight),
                countdown_time_hh: useField(_config?.announcement?.countdown_time_hh),
                countdown_time_mm: useField(_config?.announcement?.countdown_time_mm),
            },
            announcement_design: {
                message_text_color: useField({
                    value: _config?.announcement_design?.message_text_color,
                    validates: [(val) => validateHexalColor(val, "Message text")],
                }),
                message_bg_color: useField({
                    value: _config?.announcement_design?.message_bg_color,
                    validates: [(val) => validateHexalColor(val, "Message background")],
                }),

                countdown_timer_color: useField({
                    value: _config?.announcement_design?.countdown_timer_color,
                    validates: [(val) => validateHexalColor(val, "Message background")],
                }),
                message_text_style: useField<FontStylesTypes>(_config?.announcement_design?.message_text_style),
                message_text_size: useField({
                    value: _config?.announcement_design?.message_text_size,
                    validates: [(val) => validateFontSize(val, "Message background")],
                }),
                countdown_timer_size: useField({
                    value: _config?.announcement_design?.countdown_timer_size,
                    validates: [(val) => validateFontSize(val, "Message background")],
                }),
            },
            discount: {
                status: useField(_config?.discount?.status),
                title: useField({
                    value: _config?.discount?.title,
                    validates: [(val) => validateTextLength(val, "Discount Title")],
                }),
                placeholder: useField(_config?.discount?.placeholder),
                cta_text: useField({
                    value: _config?.discount?.cta_text,
                    validates: [(val) => validateTextLength(val, "Discount Button Text")],
                }),
                label_text: useField({
                    value: _config?.discount?.label_text,
                    validates: [(val) => validateTextLength(val, "Discount Label Text")],
                }),
            },
            discount_design: {
                title_color: useField({
                    value: _config?.discount_design?.title_color,
                    validates: [(val) => validateHexalColor(val, "Discount Title")],
                }),
                icon_color: useField({
                    value: _config?.discount_design?.icon_color,
                    validates: [(val) => validateHexalColor(val, "Discount Icon")],
                }),
                cta_bg_color: useField({
                    value: _config?.discount_design?.cta_bg_color,
                    validates: [(val) => validateHexalColor(val, "Discount Button")],
                }),
                cta_bg_hover_color: useField({
                    value: _config?.discount_design?.cta_bg_hover_color,
                    validates: [(val) => validateHexalColor(val, "Discount Button Hover")],
                }),
                cta_text_color: useField({
                    value: _config?.discount_design?.cta_text_color,
                    validates: [(val) => validateHexalColor(val, "Discount Button Text")],
                }),
                cta_text_hover_color: useField({
                    value: _config?.discount_design?.cta_text_hover_color,
                    validates: [(val) => validateHexalColor(val, "Discount Button Text Hover")],
                }),
                code_bg_color: useField({
                    value: _config?.discount_design?.code_bg_color || "#D9D9D9",
                    validates: [],
                }),
                code_text_color: useField({
                    value: _config?.discount_design?.code_text_color || "#303030",
                    validates: [],
                }),
            },
            additional_note: {
                status: useField(_config?.additional_note?.status),
                title: useField({
                    value: _config?.additional_note?.title,
                    validates: [(val) => validateTextLength(val, "Additional note title")],
                }),
                placeholder: useField(_config?.additional_note?.placeholder),
                cta_text: useField({
                    value: _config?.additional_note?.cta_text,
                    validates: [(val) => validateTextLength(val, "Additional note button text")],
                }),
                Additional_notes_title_text: useField({
                    value: _config?.additional_note?.Additional_notes_title_text,
                    validates: [(val) => validateTextLength(val, "Additional note label text")],
                }),
            },
            additional_note_design: {
                title_color: useField({
                    value: _config?.additional_note_design?.title_color,
                    validates: [(val) => validateHexalColor(val, "Additional note title")],
                }),
                icon_color: useField({
                    value: _config?.additional_note_design?.icon_color,
                    validates: [(val) => validateHexalColor(val, "Additional note icon")],
                }),
                cta_bg_color: useField({
                    value: _config?.additional_note_design?.cta_bg_color,
                    validates: [(val) => validateHexalColor(val, "Additional note button")],
                }),
                cta_bg_hover_color: useField({
                    value: _config?.additional_note_design?.cta_bg_hover_color,
                    validates: [(val) => validateHexalColor(val, "Additional note button hover")],
                }),
                cta_text_color: useField({
                    value: _config?.additional_note_design?.cta_text_color,
                    validates: [(val) => validateHexalColor(val, "Additional note button text")],
                }),
                cta_text_hover_color: useField({
                    value: _config?.additional_note_design?.cta_text_hover_color,
                    validates: [(val) => validateHexalColor(val, "Additional note button text hover")],
                }),
            },
            additional_product: {
                status: useField(_config?.additional_product?.status),
                product_status: useField(false),
                product_type: useField(_config?.additional_product?.product_type),
                additional_product_shipping_gift_checkbox: useField(
                    _config.additional_product?.additional_product_shipping_gift_checkbox,
                ),
                additional_product_addon_checkbox: useField(
                    _config.additional_product?.additional_product_addon_checkbox,
                ),
                shipping_protection_subtext: useField(_config?.additional_product?.shipping_protection_subtext),
                shipping_protection_title: useField({
                    value: _config?.additional_product?.shipping_protection_title,
                    validates: [
                        (val) => {
                            if (!val) {
                                return "Shipping protection title required";
                            }
                        },
                    ],
                }),
                additional_addon_subtext: useField(_config?.additional_product?.additional_addon_subtext),
                addon_modal_title: useField(_config?.additional_product?.addon_modal_title),
                addon_selector_label: useField(_config?.additional_product?.addon_selector_label),
                additional_addon_title: useField({
                    value: _config?.additional_product?.additional_addon_title,
                    validates: [
                        (val) => {
                            if (additional_product_addon_checkbox.current !== true) return;
                            if (!val) {
                                return "Additional addon title required";
                            }
                        },
                    ],
                }),
                additional_addon_products: useField({
                    value: _config?.additional_product?.additional_addon_products,
                    validates: [
                        (addonProducts) => {
                            if (additional_product_addon_checkbox.current !== true) return;
                            if (addonProducts && addonProducts.length <= 0) {
                                return "Additional addon products are required";
                            }
                        },
                    ],
                }),
                custom_fields_checkbox: useField(_config?.additional_product?.custom_fields_checkbox),
                custom_fields: useField({
                    value: _config?.additional_product?.custom_fields,
                    validates: [
                        (customField) => {
                            if (
                                custom_fields_checkbox.current !== true ||
                                additional_product_addon_checkbox.current !== true
                            )
                                return;
                            for (let i = 0; i < customField.length; i++) {
                                const field = customField[i];
                                if (!field.field_title || field.field_title.trim() === "") {
                                    return `Field #${i + 1} title is required.`;
                                }
                            }
                        },
                    ],
                }),
                shipping_protection_price: useField(_config?.additional_product?.shipping_protection_price),
                shipping_protection_images: useField(_config?.additional_product?.shipping_protection_images),
                shipping_protection_incremental_value: useField(
                    _config?.additional_product?.shipping_protection_incremental_value,
                ),
                shipping_protection_price_type: useField(_config?.additional_product?.shipping_protection_price_type),
                gift_wrapping_subtext: useField(_config?.additional_product?.gift_wrapping_subtext),
                gift_wrapping_title: useField({
                    value: _config?.additional_product?.gift_wrapping_title,
                    validates: [
                        (val: string) => {
                            if (!val) {
                                return "Gift wrapping title required";
                            }
                        },
                    ],
                }),
                gift_wrapping_price: useField(_config?.additional_product?.gift_wrapping_price),
                gift_wrapping_images: useField(_config?.additional_product?.gift_wrapping_images),
                gift_wrapping_incremental_value: useField(_config?.additional_product?.gift_wrapping_incremental_value),
                gift_wrapping_price_type: useField(_config?.additional_product?.gift_wrapping_price_type),
            },
            additional_product_design: {
                additional_product_title_size: useField({
                    value: _config?.additional_product_design?.additional_product_title_size,
                    validates: [(val) => validateFontSize(val, "Title text")],
                }),
                additional_product_description_size: useField({
                    value: _config?.additional_product_design?.additional_product_description_size,
                    validates: [(val) => validateFontSize(val, "Description text")],
                }),
                addon_modal_title_size: useField({
                    value: _config?.additional_product_design?.addon_modal_title_size,
                    validates: [(val) => validateFontSize(val, "Addon modal title")],
                }),
                additional_product_description_style: useField(
                    _config?.additional_product_design?.additional_product_description_style,
                ),
                additional_product_card_bg: useField({
                    value: _config?.additional_product_design?.additional_product_card_bg,
                    validates: [(val) => validateHexalColor(val, "Card background")],
                }),
                additional_product_title_color: useField({
                    value: _config?.additional_product_design?.additional_product_title_color,
                    validates: [(val) => validateHexalColor(val, "Title")],
                }),
                additional_product_description_color: useField({
                    value: _config?.additional_product_design?.additional_product_description_color,
                    validates: [(val) => validateHexalColor(val, "Description Title")],
                }),
                additional_product_border_color: useField({
                    value: _config?.additional_product_design?.additional_product_border_color,
                    validates: [(val) => validateHexalColor(val, "Border")],
                }),
                additional_product_border_gradient_status: useField(
                    _config?.additional_product_design?.additional_product_border_gradient_status,
                ),
                additional_product_border_gradient_direction: useField(
                    _config?.additional_product_design?.additional_product_border_gradient_direction,
                ),
                additional_product_border_gradient_color: useField(
                    _config?.additional_product_design?.additional_product_border_gradient_color,
                ),
                addon_modal_background_color: useField({
                    value: _config?.additional_product_design?.addon_modal_background_color,
                    validates: [(val) => validateHexalColor(val, "Addon Popup Background")],
                }),
                addon_modal_title_color: useField({
                    value: _config?.additional_product_design?.addon_modal_title_color,
                    validates: [(val) => validateHexalColor(val, "Addon Popup Title")],
                }),
                addon_modal_primary_button_text_color: useField({
                    value: _config?.additional_product_design?.addon_modal_primary_button_text_color,
                    validates: [(val) => validateHexalColor(val, "Addon Popup Primary Button Color")],
                }),
                addon_modal_secondary_button_text_color: useField({
                    value: _config?.additional_product_design?.addon_modal_secondary_button_text_color,
                    validates: [(val) => validateHexalColor(val, "Addon Popup Secondary Button Color")],
                }),
                addon_modal_primary_button_bg: useField({
                    value: _config?.additional_product_design?.addon_modal_primary_button_bg,
                    validates: [(val) => validateHexalColor(val, "Addon Popup Primary Button Background")],
                }),
            },
            additional_message: {
                status: useField(_config?.additional_message?.status),
                additional_msg_text: useField({
                    value: _config?.additional_message?.additional_msg_text,
                    validates: [
                        (val) => {
                            if (!val) {
                                return "Announcement text is required";
                            }
                        },
                    ],
                }),
                additional_msg_bg: useField({
                    value: _config?.additional_message?.additional_msg_bg,
                    validates: [(val) => validateHexalColor(val, "Background")],
                }),
                additional_msg_color: useField({
                    value: _config?.additional_message?.additional_msg_color,
                    validates: [(val) => validateHexalColor(val, "Message")],
                }),
                additional_msg_text_size: useField({
                    value: _config?.additional_message?.additional_msg_text_size,
                    validates: [(val) => validateFontSize(val, "Message")],
                }),
                additional_msg_text_style: useField(_config?.additional_message?.additional_msg_text_style),
            },
            trust_badges: {
                status: useField(_config?.trust_badges?.status),
                badge_size: useField(_config?.trust_badges?.badge_size),
                badges: useField({
                    value: _config?.trust_badges?.badges?.length
                        ? mergeIconStates(initialCartConfig?.trust_badges?.badges, _config?.trust_badges?.badges)
                        : initialCartConfig?.trust_badges?.badges,
                    validates: [validateTrustBadges],
                }),
            },
            terms_and_conditions: {
                status: useField(_config?.terms_and_conditions.status),
                text: useField({
                    value: _config?.terms_and_conditions.text,
                    validates: [
                        (val) => {
                            if (!val) {
                                return "Terms and Conditions text is required";
                            }
                            return undefined;
                        },
                    ],
                }),
                required_text: useField({
                    value: _config?.terms_and_conditions.required_text,
                    validates: [
                        (val) => {
                            if (!val) {
                                return "Terms and Conditions required text is required";
                            }
                            return undefined;
                        },
                    ],
                }),
            },
            terms_and_conditions_design: {
                text_color: useField({
                    value: _config?.terms_and_conditions_design.text_color,
                    validates: [(val) => validateHexalColor(val, "Text color")],
                }),
                link_color: useField({
                    value: _config?.terms_and_conditions_design.link_color,
                    validates: [(val) => validateHexalColor(val, "Link color")],
                }),
            },
            metal_bird_config: {
                status: useField(_config?.metal_bird_config?.status),
                free_gifts: useField(_config?.metal_bird_config?.free_gifts),
                product_selection_type: useField(_config?.metal_bird_config?.product_selection_type),
                products: useField(_config?.metal_bird_config?.products),
                collections: useField(_config?.metal_bird_config?.collections),
                qualifier_type: useField(_config?.metal_bird_config?.qualifier_type),
                count: useField({
                    value: _config?.metal_bird_config?.count,
                    validates: [
                        (val) => {
                            if (!val) {
                                return "Qualifier threshold is required";
                            }
                            return undefined;
                        },
                    ],
                }),
                unreached_title: useField({
                    value: _config?.metal_bird_config?.unreached_title,
                    validates: [
                        (val) => {
                            if (!val) {
                                return "Unreached text is required";
                            }
                            return undefined;
                        },
                    ],
                }),
                reached_title: useField({
                    value: _config?.metal_bird_config?.reached_title,
                    validates: [
                        (val) => {
                            if (!val) {
                                return "Reached text is required";
                            }
                            return undefined;
                        },
                    ],
                }),
                text_color: useField({
                    value: _config?.metal_bird_config?.text_color,
                    validates: [(val) => validateHexalColor(val, "Title Text")],
                }),
                text_background_color: useField({
                    value: _config?.metal_bird_config?.text_background_color,
                    validates: [(val) => validateHexalColor(val, "Text background")],
                }),
                card_background_color: useField({
                    value: _config?.metal_bird_config?.card_background_color,
                    validates: [(val) => validateHexalColor(val, "Card background")],
                }),
                outline_btn_text_color: useField({
                    value: _config?.metal_bird_config?.outline_btn_text_color,
                    validates: [(val) => validateHexalColor(val, "Outline button text")],
                }),
                outline_btn_bg_color: useField({
                    value: _config?.metal_bird_config?.outline_btn_bg_color,
                    validates: [(val) => validateHexalColor(val, "Outline button background")],
                }),
                inline_btn_text_color: useField({
                    value: _config?.metal_bird_config?.inline_btn_text_color,
                    validates: [(val) => validateHexalColor(val, "Inline button text")],
                }),
                inline_btn_bg_color: useField({
                    value: _config?.metal_bird_config?.inline_btn_bg_color,
                    validates: [(val) => validateHexalColor(val, "Inline button background")],
                }),
            },
        },
        onSubmit: async (fields) => {
            console._log("=== Cart Config ===", fields);
            setConfig(fields as unknown as CartConfigInterface);
            return { status: "success" };
        },
    });
    console._log("cartConfig fields", fields);

    UpsellProductType.current = fields?.upsell?.upsell_products_type?.value;
    additional_product_addon_checkbox.current = fields?.additional_product?.additional_product_addon_checkbox?.value;
    custom_fields_checkbox.current = fields?.additional_product?.custom_fields_checkbox?.value;

    return {
        fields,
        submit,
        reset,
        dirty,
        makeClean,
        submitErrors,
        validate,
        submitting,
    };
};

export default useCartFormFields;
